import React, { ReactNode, useRef, useState } from 'react';
import { IconCustomAurora, IconX } from '@rossum/ui/icons/tabler';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useIntl } from '../ComponentsContext';
import { CopilotComposer } from './CopilotComposer';

type CopilotDialogV2Props = {
  title: string;
  suggestingTitle: string;
  subtitle?: string;
  errorText: string;
  open: boolean;
  placeholder: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
  onSkip?: () => void;
  isSuggesting: boolean;
  isError: boolean;
  skipButtonLabel?: string;
  confirmText: string;
  renderSummary?: ReactNode;
  renderCopilotToolbar?: ReactNode;
  renderPromptSuggestions?: ReactNode;
  submitDisabled?: boolean;
  submitDisabledTooltipTitle?: string;
  value?: string;
  setValue?: (v: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
};

export const CopilotDialogV2 = ({
  title,
  suggestingTitle,
  subtitle,
  errorText,
  open,
  placeholder,
  onSubmit,
  onClose,
  onSkip,
  isSuggesting,
  isError,
  skipButtonLabel,
  confirmText,
  renderSummary,
  submitDisabled,
  submitDisabledTooltipTitle,
  renderCopilotToolbar,
  renderPromptSuggestions,
  value: controlledValue,
  setValue: controlledSetValue,
  inputRef: controlledInputRef,
}: CopilotDialogV2Props) => {
  const intl = useIntl();
  const t = useTheme();
  const [uncontrolledValue, setUncontrolledValue] = useState('');
  const uncontrolledInputRef = useRef(null);

  const value = controlledValue ?? uncontrolledValue;
  const setValue = controlledSetValue ?? setUncontrolledValue;
  const inputRef = controlledInputRef ?? uncontrolledInputRef;

  const hasValue = value.length > 0;
  const auroraLightColor = t.palette.aurora.light;

  const avatarGlowStyle = hasValue
    ? {
        border: `1px solid ${alpha(auroraLightColor, 0.39)}`,
        boxShadow: `0 0px 4px ${alpha(auroraLightColor, 0.49)}, inset 0 0 8px 4px ${alpha(auroraLightColor, 0.39)}`,
      }
    : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: {
          position: 'relative',
          overflow: 'visible',
          minWidth: '720px',
        },
      }}
      data-cy="copilot-dialog"
    >
      <Avatar
        sx={{
          bgcolor: t.palette.background.paper,
          position: 'absolute',
          right: 0,
          left: 0,
          margin: 'auto',
          top: '-32px',
          width: 64,
          height: 64,
        }}
      >
        <Box
          style={{
            ...avatarGlowStyle,
            transition:
              'border 300ms ease-in-out, box-shadow 300ms ease-in-out',
          }}
          sx={{
            borderRadius: borderRadius('xl'),
            width: 54,
            height: 54,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SvgIcon fontSize="xLarge" style={{ color: auroraLightColor }}>
            <IconCustomAurora />
          </SvgIcon>
        </Box>
      </Avatar>
      <Tooltip
        title={intl.formatMessage({ id: 'features.rules.copilotClose' })}
        placement="top"
      >
        <IconButton
          size="small"
          onClick={onClose}
          data-cy="copilot-dialog-close-button"
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
          }}
        >
          <SvgIcon fontSize="large">
            <IconX />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <DialogContent>
        <Stack spacing={2} pt={2} pb={1}>
          <Stack spacing={0.5} textAlign="center">
            <Typography variant="h5">
              {isSuggesting ? suggestingTitle : title}
            </Typography>

            {subtitle ? (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
          <Stack>
            {renderSummary ? (
              <Box maxHeight="100px">{renderSummary}</Box>
            ) : null}
            {/* Pulled up over the summary above, so it has to paint on top of it. */}
            <Box sx={{ mt: -1, position: 'relative', zIndex: 2 }}>
              <CopilotComposer
                value={value}
                setValue={setValue}
                inputRef={inputRef}
                placeholder={placeholder}
                onSubmit={onSubmit}
                isSuggesting={isSuggesting}
                isError={isError}
                errorText={errorText}
                confirmText={confirmText}
                submitDisabled={submitDisabled}
                submitDisabledTooltipTitle={submitDisabledTooltipTitle}
                renderCopilotToolbar={renderCopilotToolbar}
                autoFocus
              />
            </Box>

            <Stack
              direction="column"
              alignSelf="center"
              width="100%"
              overflow="auto"
              spacing={1}
              pt={isError ? 0 : 4}
            >
              {renderPromptSuggestions ? (
                <Stack direction="row" alignItems="center">
                  {renderPromptSuggestions}
                </Stack>
              ) : null}
              {skipButtonLabel ? (
                <Button
                  color="inherit"
                  data-cy="cancel-copilot-dialog-btn"
                  disabled={isSuggesting}
                  onClick={onSkip}
                  sx={{
                    width: 'fit-content',
                    alignSelf: 'center',
                  }}
                >
                  {skipButtonLabel}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
